import { computed, Injectable, signal } from '@angular/core';
import {
  COLOR_PALETTES,
  COLOR_SHADES,
  COLOR_SLOTS,
  ColorChoice,
  ColorPaletteName,
  ColorSlotKey,
  isHexColor,
  isPaletteName,
  shadeValue,
  translucentValue,
} from '@core/theming/color-slots';

/** localStorage key holding the user's slot choices. **/
const STORAGE_KEY = 'flowzilla.colors';

/**
 * localStorage key holding the already rendered CSS. Read by
 * `assets/js/color-theme-boot.js` so the chosen colors are in place before the first paint —
 * that script must stay free of any mapping logic, hence the pre-rendered CSS.
 * Versioned: v2 writes the `--flk-*` token names; the CSS under the old key
 * names tokens that no longer exist and is dropped.
 **/
const STORAGE_KEY_CSS = 'flowzilla.colors.v2.css';

/** Pre-rendered CSS of the legacy token names, removed on start. **/
const LEGACY_STORAGE_KEY_CSS = 'flowzilla.colors.css';

/** Id of the style element carrying the overrides. **/
const STYLE_ELEMENT_ID = 'flowzilla-color-theme';

type ColorChoices = Partial<Record<ColorSlotKey, ColorChoice>>;

/**
 * Lets the user recolor the application at runtime, without a rebuild.
 *
 * Overrides are written as a `:root` block into a single style element that is
 * appended after the compiled stylesheet, so it wins over the defaults of
 * `@group24ag/flowkit-tokens`. A slot without a choice simply falls back to
 * whatever the token package says — that is what {@link reset} restores.
 **/
@Injectable({ providedIn: 'root' })
export class ColorThemeService {
  /** PROPERTIES **/
  /** The user's choices; a missing slot means "use the stylesheet default". **/
  private readonly _choices = signal<ColorChoices>({});

  /****/
  readonly choices = this._choices.asReadonly();

  /** Whether at least one slot deviates from the stylesheet default. **/
  readonly customized = computed(() => Object.keys(this._choices()).length > 0);

  /**
   * Bumped whenever the applied colors change, so the resolved-value computeds
   * below re-read from the DOM.
   **/
  private readonly _revision = signal(0);

  /**
   * Base color of every palette, read straight from the token package.
   * Constant for the lifetime of the app, so it is resolved once.
   **/
  readonly paletteColors = computed<Record<ColorPaletteName, string>>(() => {
    const colors = {} as Record<ColorPaletteName, string>;

    for (const palette of COLOR_PALETTES) {
      colors[palette] = this.readVariable(`--flk-palette-${palette}-500`);
    }

    return colors;
  });

  /**
   * The color every slot currently renders with — no matter whether it comes
   * from an override or from the token package. A hex value where possible, so
   * it can feed a color input.
   **/
  readonly resolvedColors = computed<Record<ColorSlotKey, string>>(() => {
    this._revision();

    const choices = this._choices();
    const colors = {} as Record<ColorSlotKey, string>;

    for (const slot of COLOR_SLOTS) {
      const choice = choices[slot.key];

      if (choice?.kind === 'custom') {
        colors[slot.key] = choice.hex;
        continue;
      }

      const value = this.readVariable(`${slot.tokens[0]}-500`);
      colors[slot.key] = isHexColor(value) ? value : '#000000';
    }

    return colors;
  });

  /**
   * The palette every slot resolves to, matched by color value so the
   * stylesheet default is detected too. Null for a freely picked color.
   **/
  readonly activePalettes = computed<
    Record<ColorSlotKey, ColorPaletteName | null>
  >(() => {
    const choices = this._choices();
    const resolved = this.resolvedColors();
    const palettes = this.paletteColors();

    const active = {} as Record<ColorSlotKey, ColorPaletteName | null>;

    for (const slot of COLOR_SLOTS) {
      if (choices[slot.key]?.kind === 'custom') {
        active[slot.key] = null;
        continue;
      }

      const color = resolved[slot.key].toLowerCase();

      active[slot.key] =
        COLOR_PALETTES.find(
          palette => palettes[palette].toLowerCase() === color
        ) ?? null;
    }

    return active;
  });

  constructor() {
    this.restore();
  }

  /** Points a slot at one of the shipped palettes. **/
  setPalette(key: ColorSlotKey, palette: ColorPaletteName): void {
    this.setChoice(key, { kind: 'palette', palette });
  }

  /** Points a slot at a freely picked color; shades are derived from it. **/
  setCustom(key: ColorSlotKey, hex: string): void {
    if (!isHexColor(hex)) return;
    this.setChoice(key, { kind: 'custom', hex: hex.toLowerCase() });
  }

  /** Drops the override of a slot, restoring the stylesheet default. **/
  reset(key: ColorSlotKey): void {
    const next = { ...this._choices() };
    delete next[key];
    this._choices.set(next);
    this.apply();
  }

  /** Drops every override. **/
  resetAll(): void {
    this._choices.set({});
    this.apply();
  }

  /** The choice of a slot, or null when it uses the stylesheet default. **/
  choiceOf(key: ColorSlotKey): ColorChoice | null {
    return this._choices()[key] ?? null;
  }

  private setChoice(key: ColorSlotKey, choice: ColorChoice): void {
    this._choices.set({ ...this._choices(), [key]: choice });
    this.apply();
  }

  /** Renders the overrides into the style element and persists them. **/
  private apply(): void {
    const css = this.render();

    this.styleElement().textContent = css;
    this._revision.update(revision => revision + 1);

    try {
      const choices = this._choices();

      if (Object.keys(choices).length === 0) {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_KEY_CSS);
        return;
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(choices));
      localStorage.setItem(STORAGE_KEY_CSS, css);
    } catch {
      // Storage unavailable (private mode, quota) — the colors still apply for
      // this session, they just will not survive a reload.
    }
  }

  /** Builds the `:root` block for the current choices. **/
  private render(): string {
    const choices = this._choices();
    const declarations: string[] = [];

    for (const slot of COLOR_SLOTS) {
      const choice = choices[slot.key];
      if (!choice) continue;

      for (const token of slot.tokens) {
        for (const shade of COLOR_SHADES) {
          declarations.push(`${token}-${shade}:${shadeValue(choice, shade)};`);
        }

        if (slot.translucent) {
          declarations.push(`${token}-500-02:${translucentValue(choice)};`);
        }
      }
    }

    return declarations.length ? `:root{${declarations.join('')}}` : '';
  }

  /**
   * The style element carrying the overrides. Created lazily and appended last
   * so it outranks the `:root` block of the compiled stylesheet.
   **/
  private styleElement(): HTMLStyleElement {
    const existing = document.getElementById(STYLE_ELEMENT_ID);
    if (existing instanceof HTMLStyleElement) return existing;

    const element = document.createElement('style');
    element.id = STYLE_ELEMENT_ID;
    document.head.appendChild(element);

    return element;
  }

  private readVariable(name: string): string {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();
  }

  /** Reads the persisted choices, dropping anything unknown or malformed. **/
  private restore(): void {
    let raw: string | null = null;

    try {
      // The choices themselves are unchanged; only the CSS rendered from them
      // used the old names, and apply() below writes it anew under the v2 key.
      localStorage.removeItem(LEGACY_STORAGE_KEY_CSS);
      raw = localStorage.getItem(STORAGE_KEY);
    } catch {
      return;
    }

    if (!raw) return;

    const choices: ColorChoices = {};

    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;

      for (const slot of COLOR_SLOTS) {
        const choice = parsed[slot.key] as ColorChoice | undefined;
        if (!choice) continue;

        if (choice.kind === 'palette' && isPaletteName(choice.palette)) {
          choices[slot.key] = { kind: 'palette', palette: choice.palette };
        } else if (choice.kind === 'custom' && isHexColor(choice.hex)) {
          choices[slot.key] = { kind: 'custom', hex: choice.hex.toLowerCase() };
        }
      }
    } catch {
      // Corrupt payload — fall back to the stylesheet defaults.
    }

    this._choices.set(choices);
    this.apply();
  }
}
